// lib/koi-pond-v3/assets/moon.ts
//
// Moon as a textured disc above the water surface, paired with a
// DirectionalLight that lights the koi (Phase 8) and the caustics
// (T7.1 modulates by moon direction). The disc itself is a flat
// plane facing the camera; the halo is a larger billboard plane
// behind it with additive blending and radial falloff.
//
// World placement: (4.5, 8.0, -4.0) — upper-right of frame per
// composition spec. Far above the water surface (T7.3 places water
// at y=1.0) so it reads as "in the sky" rather than on the surface.
// Z=-4 pushes it away from camera so the disc has room to be
// substantial without dominating.

import {
  PlaneGeometry,
  Mesh,
  ShaderMaterial,
  AdditiveBlending,
  DirectionalLight,
  Vector3,
  Color,
  DoubleSide,
} from 'three';
import { paletteVec3, palette } from '../palette';

export interface MoonAssets {
  disc: Mesh;
  halo: Mesh;
  light: DirectionalLight;
  lightTarget: import('three').Object3D;
  moonGroupPos: Vector3;
  moonDir: Vector3;
  // T10.3 — set the moon's brightness (cloud-dimming): 1 = full, 0.7 = dimmed.
  setDim: (v: number) => void;
  // Dragon-gate bloom: 0 = rest, ~1 = detonation, small negative = anticipation.
  setBloom: (v: number) => void;
}

export function makeMoon(): MoonAssets {
  // T7.6c — moon lowered from y=8 to y=5 (closer to horizon). Real
  // moonlit pond photos almost always have the moon low because the
  // moon's reflection only forms a path on water when the moon sits
  // near the horizon — a moon high overhead creates a tiny localized
  // bright spot directly below, not the iconic shimmering trail.
  //
  // KOI-VISIBILITY RECOMPOSE: the gaze tilt-down (camera lookAt y
  // 2.0→1.0) pushed the moon toward the top edge, clipping its halo.
  // Lowered y=5→4 to re-seat it comfortably in the upper-right; the
  // lower elevation also grazes the moonlight more across the koi
  // flanks (better fresnel rim + SSS read) and lengthens the
  // reflection trail.
  const moonGroupPos = new Vector3(4.0, 4.0, -3.5);

  // Disc — solid moonlight cream with subtle limb darkening
  const discGeom = new PlaneGeometry(1.4, 1.4);
  const discMat = new ShaderMaterial({
    uniforms: { uDim: { value: 1 }, uBloom: { value: 0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      uniform float uDim;
      uniform float uBloom;
      varying vec2 vUv;
      void main() {
        vec2 d = vUv - vec2(0.5);
        float r = length(d) * 2.0;
        if (r > 1.0) discard;
        // Subtle limb darkening — dimmer at disc edge, brighter at center.
        // Reads as a faint hint of dimensionality without being a sphere.
        float i = mix(1.0, 0.85, smoothstep(0.6, 1.0, r));
        vec3 color = ${paletteVec3('moonlight')} * i * uDim * (1.0 + 2.2 * uBloom);
        gl_FragColor = vec4(color, 1.0);
      }
    `,
    transparent: false,
    // T7.6a — render both sides. The disc plane lies in XY (normal
    // +Z by default) but camera looks at it from a non-+Z direction,
    // so default FrontSide culling would hide it.
    side: DoubleSide,
  });
  const disc = new Mesh(discGeom, discMat);
  disc.position.copy(moonGroupPos);

  // Halo — larger plane with additive radial glow. Sits BEHIND the
  // disc (slight z offset) so the disc's solid cream sits crisply
  // over the halo's diffuse warm amber. Additive blending means the
  // halo brightens whatever's behind it without darkening anything.
  //
  // T7.6e — halo plane enlarged 4x4 → 8x8 + explicit discard at the
  // visible-glow boundary. The previous 4x4 plane had non-trivial
  // alpha at its mid-edges (r=1.0 in UV space → 0.02 alpha), which
  // additive-blended onto dark sky as a visible rectangle outline.
  // Now the plane bounds extend OUTSIDE the visible glow extent —
  // the discard at r > 1.0 hard-cuts the glow into a circle, plane
  // edges land in already-zero-alpha territory.
  const haloGeom = new PlaneGeometry(8.0, 8.0);
  const haloMat = new ShaderMaterial({
    uniforms: { uDim: { value: 1 }, uBloom: { value: 0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      uniform float uDim;
      uniform float uBloom;
      varying vec2 vUv;
      void main() {
        // Plane is 8x8 world units; visible glow lives in the central
        // 4x4 circle (r in UV-space scaled 0..1 across half the plane).
        // r > 1.0 = outside the visible glow → discard before falloff
        // computes, eliminating the rectangle-outline artifact.
        vec2 d = vUv - vec2(0.5);
        float r = length(d) * 4.0; // 4.0 because half the plane is 4 world units → r=1 at world radius 4
        if (r > 1.0) discard;
        // Gaussian falloff inside the cone. Peak alpha 0.65 at center,
        // fades smoothly to 0 at the discard boundary.
        float a = exp(-r * r * 3.5) * 0.65 * uDim;
        a *= (1.0 + 3.0 * uBloom);
        vec3 color = ${paletteVec3('moonHalo')};
        gl_FragColor = vec4(color, a);
      }
    `,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    // T7.6a — same back-face-culling fix as disc.
    side: DoubleSide,
  });
  const halo = new Mesh(haloGeom, haloMat);
  halo.position.copy(moonGroupPos);
  halo.position.z -= 0.01;

  // Light source — DirectionalLight from moon toward origin. Koi
  // material (Phase 8) reads this as the key light direction for
  // top-lighting + fresnel rim. Caustic floor (T7.1) reads the
  // moon's WORLD POSITION via uMoonWorldDir for caustic brightness
  // modulation.
  const light = new DirectionalLight(new Color(palette.moonlight.hex), 1.7);
  light.position.copy(moonGroupPos);
  light.target.position.set(0, 0, 0);

  // moonDir is the normalized vector from origin toward moon —
  // what the caustic floor consumes via uMoonWorldDir. Normalize
  // here so callers don't have to.
  const moonDir = moonGroupPos.clone().normalize();

  const baseLight = light.intensity;
  // setDim (cloud-dimming) and setBloom (dragon-gate) compose on the light:
  // intensity = base * dim * (1 + 0.8*bloom). Each writes its own factor.
  let dimFactor = 1;
  let bloomFactor = 0;
  const applyLight = () => {
    light.intensity = baseLight * dimFactor * (1 + 0.8 * Math.max(0, bloomFactor));
  };
  const setDim = (v: number) => {
    dimFactor = v;
    discMat.uniforms.uDim.value = v;
    haloMat.uniforms.uDim.value = v;
    applyLight();
  };
  // Dragon-gate bloom (rest v=0 = no-op): brighter disc/halo + gentle swell.
  // Accepts small negative values for the anticipation "breath in".
  const setBloom = (v: number) => {
    bloomFactor = v;
    discMat.uniforms.uBloom.value = v;
    haloMat.uniforms.uBloom.value = v;
    disc.scale.setScalar(1 + 0.12 * v);
    halo.scale.setScalar(1 + 0.18 * v);
    applyLight();
  };

  return { disc, halo, light, lightTarget: light.target, moonGroupPos, moonDir, setDim, setBloom };
}
